/**
 * GridSystem - Manages the geographic grid overlay
 */

export class GridSystem {
  constructor(options = {}) {
    this.latStep = options.latStep || 15;
    this.lonStep = options.lonStep || 30;
    this.gridCells = [];
    this.bounds = {
      north: 90,
      south: -90,
      east: 180,
      west: -180
    };
    this.apiService = null;
  }

  /**
   * Allow grid to access API service for future enhancements
   */
  setAPIService(apiService) {
    this.apiService = apiService;
  }

  /**
   * Generate the grid cells covering the Earth
   */
  generateGrid() {
    this.gridCells = [];

    for (let lat = this.bounds.south; lat < this.bounds.north; lat += this.latStep) {
      for (let lon = this.bounds.west; lon < this.bounds.east; lon += this.lonStep) {
        const cell = this.createGridCell(lat, lon);
        this.gridCells.push(cell);
      }
    }

    if (typeof process === 'undefined' || process.env?.NODE_ENV !== 'test') {
      console.log(`Generated ${this.gridCells.length} grid cells`);
    }
    return this.gridCells;
  }

  /**
   * Create a single grid cell
   */
  createGridCell(lat, lon) {
    const bounds = [
      [lat, lon],
      [lat + this.latStep, lon + this.lonStep]
    ];

    const centerLatRaw = lat + this.latStep / 2;
    const centerLonRaw = lon + this.lonStep / 2;
    const equatorTolerance = this.latStep / 2;
    const meridianTolerance = this.lonStep / 2;

    const crossesEquator = lat < 0 && (lat + this.latStep) >= 0;
    const crossesMeridian = lon < 0 && (lon + this.lonStep) >= 0;

    const centerLat = crossesEquator && Math.abs(centerLatRaw) <= equatorTolerance
      ? 0
      : centerLatRaw;

    const centerLon = crossesMeridian && Math.abs(centerLonRaw) <= meridianTolerance
      ? 0
      : centerLonRaw;

    return {
      id: `cell_${lat}_${lon}`,
      bounds,
      centerLat,
      centerLon,
      lat: centerLat,
      lon: centerLon,
      area: this.calculateCellArea(lat, lon),
      coordinates: {
        topLeft: [lat + this.latStep, lon],
        topRight: [lat + this.latStep, lon + this.lonStep],
        bottomLeft: [lat, lon],
        bottomRight: [lat, lon + this.lonStep]
      }
    };
  }

  /**
   * Calculate the approximate area of a grid cell in square kilometers
   */
  calculateCellArea(lat, lon) {
    // Simplified area calculation
    const earthRadius = 6371; // km
    const latRadians = (lat * Math.PI) / 180;
    const latStepRadians = (this.latStep * Math.PI) / 180;
    const lonStepRadians = (this.lonStep * Math.PI) / 180;

    const area = Math.pow(earthRadius, 2) * Math.abs(lonStepRadians) *
                 Math.abs(Math.sin(latRadians + latStepRadians) - Math.sin(latRadians));

    return Math.round(area);
  }

  /**
   * Get all grid cells
   */
  getGridCells() {
    return this.gridCells;
  }

  /**
   * Find the grid cell containing a specific coordinate
   */
  getCellForCoordinate(lat, lon) {
    return this.gridCells.find(cell => {
      return lat >= cell.bounds[0][0] && lat < cell.bounds[1][0] &&
             lon >= cell.bounds[0][1] && lon < cell.bounds[1][1];
    });
  }

  /**
   * Get grid cells within a bounding box
   */
  getCellsInBounds(bounds) {
    return this.gridCells.filter(cell => {
      return this.cellIntersectsBounds(cell, bounds);
    });
  }

  /**
   * Check if a cell intersects with given bounds
   */
  cellIntersectsBounds(cell, bounds) {
    const cellBounds = cell.bounds;
    return !(cellBounds[1][0] <= bounds.south ||
             cellBounds[0][0] >= bounds.north ||
             cellBounds[1][1] <= bounds.west ||
             cellBounds[0][1] >= bounds.east);
  }

  /**
   * Get neighboring cells for a given cell
   */
  getNeighboringCells(targetCell, radius = 1) {
    const neighbors = [];

    for (const cell of this.gridCells) {
      if (cell.id === targetCell.id) continue;

      const latDistance = Math.abs(cell.centerLat - targetCell.centerLat);
      const lonDistance = Math.abs(cell.centerLon - targetCell.centerLon);

      if (latDistance <= this.latStep * radius &&
          lonDistance <= this.lonStep * radius) {
        neighbors.push(cell);
      }
    }

    return neighbors;
  }

  /**
   * Calculate distance between two cells
   */
  calculateDistance(cell1, cell2) {
    const lat1 = (cell1.centerLat * Math.PI) / 180;
    const lon1 = (cell1.centerLon * Math.PI) / 180;
    const lat2 = (cell2.centerLat * Math.PI) / 180;
    const lon2 = (cell2.centerLon * Math.PI) / 180;

    const dLat = lat2 - lat1;
    const dLon = lon2 - lon1;

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = 6371 * c; // Distance in kilometers

    return Math.round(distance);
  }

  /**
   * Get grid statistics
   */
  getGridStats() {
    const totalArea = this.gridCells.reduce((sum, cell) => sum + cell.area, 0);
    const avgArea = totalArea / this.gridCells.length;

    return {
      totalCells: this.gridCells.length,
      totalArea: Math.round(totalArea),
      averageArea: Math.round(avgArea),
      latStep: this.latStep,
      lonStep: this.lonStep,
      bounds: { ...this.bounds }
    };
  }

  /**
   * Update grid resolution
   */
  updateResolution(latStep, lonStep) {
    this.latStep = latStep;
    this.lonStep = lonStep;
    this.generateGrid();
    return this.gridCells;
  }

  /**
   * Get cells along a specific latitude or longitude
   */
  getCellsAlongLatitude(latitude, tolerance = 5) {
    if (!this.gridCells.length) return [];

    let minDifference = Infinity;

    this.gridCells.forEach(cell => {
      const southLat = cell.bounds[0][0];
      const northLat = cell.bounds[1][0];

      const withinBounds = latitude >= southLat - tolerance && latitude <= northLat + tolerance;
      if (!withinBounds) return;

      const difference = Math.abs(cell.centerLat - latitude);
      if (difference < minDifference) {
        minDifference = difference;
      }
    });

    if (!Number.isFinite(minDifference)) {
      return [];
    }

    const effectiveTolerance = Math.max(tolerance, minDifference);

    return this.gridCells.filter(cell => {
      const southLat = cell.bounds[0][0];
      const northLat = cell.bounds[1][0];
      const withinBounds = latitude >= southLat - tolerance && latitude <= northLat + tolerance;
      if (!withinBounds) return false;

      return Math.abs(cell.centerLat - latitude) <= effectiveTolerance + 1e-6;
    });
  }

  getCellsAlongLongitude(longitude, tolerance = 10) {
    if (!this.gridCells.length) return [];

    let minDifference = Infinity;

    this.gridCells.forEach(cell => {
      const westLon = cell.bounds[0][1];
      const eastLon = cell.bounds[1][1];

      const withinBounds = longitude >= westLon - tolerance && longitude <= eastLon + tolerance;
      if (!withinBounds) return;

      const difference = Math.abs(cell.centerLon - longitude);
      if (difference < minDifference) {
        minDifference = difference;
      }
    });

    if (!Number.isFinite(minDifference)) {
      return [];
    }

    const effectiveTolerance = Math.max(tolerance, minDifference);

    return this.gridCells.filter(cell => {
      const westLon = cell.bounds[0][1];
      const eastLon = cell.bounds[1][1];
      const withinBounds = longitude >= westLon - tolerance && longitude <= eastLon + tolerance;
      if (!withinBounds) return false;

      return Math.abs(cell.centerLon - longitude) <= effectiveTolerance + 1e-6;
    });
  }

  /**
   * Find the closest cell to a given coordinate
   */
  getClosestCell(lat, lon) {
    let closestCell = null;
    let minDistance = Infinity;

    for (const cell of this.gridCells) {
      const distance = this.calculateCoordinateDistance(
        lat, lon,
        cell.centerLat, cell.centerLon
      );

      if (distance < minDistance) {
        minDistance = distance;
        closestCell = cell;
      }
    }

    return closestCell;
  }

  /**
   * Calculate distance between two coordinates
   */
  calculateCoordinateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos((lat1 * Math.PI) / 180) *
              Math.cos((lat2 * Math.PI) / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Export grid data
   */
  exportGrid(format = 'json') {
    const data = {
      metadata: {
        generated: new Date().toISOString(),
        version: '1.0.0',
        cellCount: this.gridCells.length,
        resolution: {
          latStep: this.latStep,
          lonStep: this.lonStep
        },
        bounds: this.bounds
      },
      cells: this.gridCells
    };

    switch (format.toLowerCase()) {
      case 'json':
        return JSON.stringify(data, null, 2);

      case 'csv':
        return this.exportToCSV();

      case 'geojson':
        return this.exportToGeoJSON();

      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  }

  /**
   * Export to CSV format
   */
  exportToCSV() {
    const headers = ['id', 'centerLat', 'centerLon', 'area', 'bounds'];
    const rows = [headers.join(',')];

    for (const cell of this.gridCells) {
      const row = [
        cell.id,
        cell.centerLat.toFixed(4),
        cell.centerLon.toFixed(4),
        cell.area,
        `"${JSON.stringify(cell.bounds)}"`
      ];
      rows.push(row.join(','));
    }

    return rows.join('\n');
  }

  /**
   * Export to GeoJSON format
   */
  exportToGeoJSON() {
    const features = this.gridCells.map(cell => ({
      type: 'Feature',
      properties: {
        id: cell.id,
        centerLat: cell.centerLat,
        centerLon: cell.centerLon,
        area: cell.area
      },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [cell.bounds[0][1], cell.bounds[0][0]],
          [cell.bounds[1][1], cell.bounds[0][0]],
          [cell.bounds[1][1], cell.bounds[1][0]],
          [cell.bounds[0][1], cell.bounds[1][0]],
          [cell.bounds[0][1], cell.bounds[0][0]]
        ]]
      }
    }));

    return JSON.stringify({
      type: 'FeatureCollection',
      features
    }, null, 2);
  }

  /**
   * Clear the current grid
   */
  clearGrid() {
    this.gridCells = [];
  }

  /**
   * Check if grid is generated
   */
  hasGrid() {
    return this.gridCells.length > 0;
  }
}
